// @ts-strict-ignore
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { antGrey } from '@/components/styles/globalStyles';
import { RootState } from '../reducers/reducers';
import { AppComponentProps } from '../typescriptHelpers';
import { selectAppSettingAutomations, selectAppStoreType } from '../selectors/selectors';
import { automationDisplayNameForAutomationId } from '../helpers/displayHelpers';
import { Automation, AutomationType } from '../types';
import { trySetAutomationPause } from '../actions/app';
import { AppUpdatable } from '../actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../utils/globalMessage';

const DEFAULT_AUTOMATION_TYPES = [
    AutomationType.AutoKickoffRelease,
    AutomationType.AutoSubmitRelease,
    AutomationType.AutoRelease,
];

const isAutomationEnabled = (automation: Automation) =>
    automation.enabled || Object.values(automation.configPerReleaseType ?? {}).some((override) => override.enabled);

interface PauseAutomationsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const PauseAutomationsModal: React.FC<PauseAutomationsModalProps> = (props: PauseAutomationsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [pendingPause, setPendingPause] = React.useState(false);

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const enabledAutomations = React.useMemo(() => automations?.filter(isAutomationEnabled) ?? [], [automations]);

    const isLoading = useIsLoading(AppUpdatable.SetAutomationPause, props.appId);
    const error = useError(AppUpdatable.SetAutomationPause, props.appId);
    const success = useUpdated(AppUpdatable.SetAutomationPause, props.appId);

    const automationOptions = React.useMemo(
        () =>
            enabledAutomations.map((automation) => ({
                value: automation.type,
                label: automationDisplayNameForAutomationId(automation.type, appStoreType) ?? automation.name,
            })),
        [enabledAutomations, appStoreType],
    );

    const defaultSelectedAutomationIds = React.useMemo(() => {
        const availableTypes = new Set(enabledAutomations.map((automation) => automation.type));
        return DEFAULT_AUTOMATION_TYPES.filter((automationType) => availableTypes.has(automationType));
    }, [enabledAutomations]);

    const allEnabledAutomationTypes = React.useMemo(
        () => enabledAutomations.map((automation) => automation.type),
        [enabledAutomations],
    );

    const handleSelectAll = () => {
        form.setFieldsValue({ automationIds: allEnabledAutomationTypes });
    };

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue({ automationIds: defaultSelectedAutomationIds });
        }
    }, [props.open, defaultSelectedAutomationIds, form]);

    React.useEffect(() => {
        if (!props.open) {
            setPendingPause(false);
        }
    }, [props.open]);

    React.useEffect(() => {
        if (success && pendingPause) {
            globalMessage.success('Automations paused!');
            setPendingPause(false);
            props.onSuccess();
        }
    }, [success, pendingPause]);

    React.useEffect(() => {
        if (error && pendingPause) {
            globalMessage.error(error);
            setPendingPause(false);
        }
    }, [error, pendingPause]);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            setPendingPause(true);
            dispatch(
                trySetAutomationPause(
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        enabled: false,
                        automationIds: values.automationIds,
                    },
                    props.appId,
                ),
            );
        });
    };

    return (
        <Modal
            title="Pause automations"
            open={props.open}
            onCancel={props.onCancel}
            onOk={handleSubmit}
            okText="Pause automations"
            confirmLoading={isLoading}
            destroyOnClose
        >
            <Text style={{ display: 'block', marginBottom: 16 }}>
                Runway will pause the selected automations for all current and upcoming releases.{' '}
                <a href="https://docs.runway.team/automations/types-of-automations" target="_blank" rel="noreferrer">
                    Learn more
                </a>
            </Text>
            <Form form={form} layout="vertical">
                <Form.Item style={{ marginBottom: 0 }}>
                    <Flex vertical>
                        <Flex style={{ paddingBottom: 8 }} justify="space-between" align="center">
                            <Flex align="center" gap={6}>
                                <Text>Automations to pause</Text>
                                <Tooltip title="Only currently enabled automations are shown in this list.">
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Tooltip>
                            </Flex>
                            {allEnabledAutomationTypes.length > 1 && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={handleSelectAll}
                                    style={{ padding: 0, height: 'fit-content' }}
                                >
                                    Select all
                                </Button>
                            )}
                        </Flex>
                        <Form.Item
                            name="automationIds"
                            noStyle
                            rules={[{ required: true, message: 'Select at least one automation' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select automations"
                                options={automationOptions}
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PauseAutomationsModal;
